import React, { useRef } from 'react';
import {
  MIN_MINUTES,
  MAX_MINUTES,
  MIN_CLIP_MINUTES,
  minuteToPx,
  pxToMinute,
  snap,
  formatHHMM,
} from './timelineGeometry';

/**
 * ClipBlock — a single draggable clip inside a content lane.
 *
 * Drag modes:
 *  - body         → move start+end together (time only; no vertical-drag
 *                   lane change, because lane = content, which is edited
 *                   via the inspector, not by dropping clips between rows).
 *  - handle.left  → resize from the left.
 *  - handle.right → resize from the right.
 *
 * Positioning uses the parent-supplied windowRange {startMin, endMin} so
 * the clip stays aligned with the shared timescale (Today's Timeline).
 */
export default function ClipBlock({
  intent,
  laneIndex,
  laneHeightPx,
  windowRange,
  containerWidth,
  containerRef,
  color,
  displayName,
  selected,
  onSelect,
  onLocalPatch,
  onCommit,
  dayOffsetMinutes = 0,
  onDoubleClickDrill = null,
}) {
  const elRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.button !== 0 || e.detail > 1) return; // let double-click bubble
    const target = e.target;
    const isLeftHandle = target.dataset?.handle === 'left';
    const isRightHandle = target.dataset?.handle === 'right';
    const mode = isLeftHandle ? 'resize-left' : isRightHandle ? 'resize-right' : 'move';

    e.stopPropagation();
    e.preventDefault();
    onSelect?.(intent.id);

    const container = containerRef?.current;
    const liveWidth = container?.clientWidth || containerWidth || 0;
    if (!liveWidth) return;

    const startX = e.clientX;
    const origStart = intent.startMinutes;
    const origEnd = intent.endMinutes;
    let lastPatch = null;

    // In multi-day windows the container spans many days on the x-axis;
    // a pixel delta in that frame is "absolute minutes across all days",
    // which would drag across calendar boundaries. Normalise the drag
    // sensitivity to the within-a-single-day scale so 1 day of axis
    // width = 1440 min of drag, matching the Day-view feel regardless of
    // how wide the Week/Month axis is.
    const spanMin = Math.max(1, (windowRange?.endMin ?? MAX_MINUTES) - (windowRange?.startMin ?? MIN_MINUTES));
    const windowDays = Math.max(1, Math.round(spanMin / 1440));

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const rawDMin =
        pxToMinute(dx, liveWidth, windowRange) -
        pxToMinute(0, liveWidth, windowRange);
      const dMin = rawDMin / windowDays;
      let patch = null;

      if (mode === 'move') {
        const len = origEnd - origStart;
        let ns = snap(origStart + dMin);
        ns = Math.max(MIN_MINUTES, Math.min(MAX_MINUTES - len, ns));
        const ne = ns + len;
        patch = {
          startMinutes: ns,
          endMinutes: ne,
          startTime: formatHHMM(ns),
          endTime: formatHHMM(ne),
        };
      } else if (mode === 'resize-left') {
        let ns = snap(origStart + dMin);
        ns = Math.max(MIN_MINUTES, Math.min(origEnd - MIN_CLIP_MINUTES, ns));
        patch = { startMinutes: ns, startTime: formatHHMM(ns) };
      } else if (mode === 'resize-right') {
        let ne = snap(origEnd + dMin);
        ne = Math.max(origStart + MIN_CLIP_MINUTES, Math.min(MAX_MINUTES, ne));
        patch = { endMinutes: ne, endTime: formatHHMM(ne) };
      }

      if (patch) {
        lastPatch = patch;
        onLocalPatch?.(intent.id, patch);
      }
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (lastPatch) {
        const serverPatch = {};
        if ('startTime' in lastPatch) serverPatch.startTime = lastPatch.startTime;
        if ('endTime' in lastPatch) serverPatch.endTime = lastPatch.endTime;
        onCommit?.(intent.id, serverPatch);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Position & size relative to the shared window. Clips outside the window
  // are clamped to its edges so they remain partially visible (with a
  // visual cue from the extension that they continue beyond the window).
  const leftPx = minuteToPx(intent.startMinutes + dayOffsetMinutes, containerWidth || 0, windowRange);
  const rightPx = minuteToPx(intent.endMinutes + dayOffsetMinutes, containerWidth || 0, windowRange);
  const containerW = containerWidth || 0;

  // If the clip is entirely outside the visible window, don't render.
  if (rightPx < 0 || leftPx > containerW) return null;

  // At Week/Month zoom a 1h clip is ~3px; honour that instead of forcing a
  // 24px minimum that would visually overshoot the real end time (and
  // bleed past the NOW line into times the clip has already ended). The
  // 24px target is only valid in Day view, where a single minute occupies
  // many pixels and 24px stays well inside the actual clip range.
  // Detect multi-day via the window span instead of dayOffsetMinutes —
  // today's chip (dayOffset=0) in Week view also needs the narrow min.
  const winSpanMin = Math.max(1, (windowRange?.endMin ?? MAX_MINUTES) - (windowRange?.startMin ?? MIN_MINUTES));
  const isMultiDayWindow = winSpanMin > 1440;
  const minWidthPx = isMultiDayWindow ? 4 : 24;
  const clampedLeft = Math.max(0, leftPx);
  const clampedWidth = Math.max(minWidthPx, Math.min(containerW, rightPx) - clampedLeft);

  const topPx = (laneHeightPx - 32) / 2; // center a 32px-tall clip inside the lane

  const bg = color?.bg || '#2d7d5a';
  const border = selected ? '#4f8cff' : color?.border || '#1f5a40';

  return (
    <div
      ref={elRef}
      className={[
        'timeline-editor__clip',
        selected && 'timeline-editor__clip--selected',
      ].filter(Boolean).join(' ')}
      style={{
        left: `${clampedLeft}px`,
        width: `${clampedWidth}px`,
        top: `${topPx}px`,
        height: '32px',
        backgroundColor: bg,
        borderColor: border,
      }}
      role="button"
      aria-label={`Clip ${intent.name}, ${intent.startTime} to ${intent.endTime}`}
      tabIndex={selected ? 0 : -1}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(intent.id);
      }}
      onDoubleClick={onDoubleClickDrill ? (e) => { e.stopPropagation(); onDoubleClickDrill(); } : undefined}
      title={`${displayName || intent.name} · ${intent.startTime}–${intent.endTime}${onDoubleClickDrill ? ' · double-click to open this day' : ''}`}
      data-lane-index={laneIndex}
      data-clip-id={intent.id}
    >
      <div
        className="timeline-editor__clip-handle timeline-editor__clip-handle--left"
        data-handle="left"
        aria-hidden="true"
      />
      <span className="timeline-editor__clip-name">{displayName || intent.name}</span>
      <span className="timeline-editor__clip-time">
        {intent.startTime}–{intent.endTime}
      </span>
      {(() => {
        // Tiny span badge so users can tell a multi-day schedule apart from a
        // today-only one without opening the inspector. Hidden for single-day
        // AND hidden for per-day pulses (Week/Month) where each pulse already
        // represents one day — badge becomes noise + overflows narrow pulses.
        if (dayOffsetMinutes > 0) return null;
        if (!intent.startDate || !intent.endDate) return null;
        const a = new Date(intent.startDate);
        const b = new Date(intent.endDate);
        if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
        const days = Math.max(1, Math.round((b - a) / 86400000) + 1);
        if (days <= 1) return null;
        return <span className="timeline-editor__clip-span">{days}d</span>;
      })()}
      <div
        className="timeline-editor__clip-handle timeline-editor__clip-handle--right"
        data-handle="right"
        aria-hidden="true"
      />
    </div>
  );
}
